import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { bulkCreateUsersAPI, getAdminsForServerAPI } from '../services/serverService';

const JOB_TITLES_FALLBACK = ['Care Worker', 'Senior Carer', 'Nurse', 'Manager', 'Coordinator'];

const BulkUploadModal = ({ serverId, role, closeModal, refreshList }) => {
    const [parsedData, setParsedData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState(null);

    // --- BATCH DEFAULTS STATE ---
    const [admins, setAdmins] = useState([]);
    const [batchSettings, setBatchSettings] = useState({
        jobTitle: 'Care Worker',
        assignedAdminId: '',
        dob: '1900-01-01',
        addressText: 'TBD'
    });

    // Fetch Admins for the Dropdown if we are uploading Employees
    useEffect(() => {
        if (role === 'employee') {
            const fetchAdmins = async () => {
                try {
                    const response = await getAdminsForServerAPI(serverId);
                    setAdmins(response.data?.data || []);
                } catch (error) {
                    console.error("Failed to fetch admins for batch settings", error);
                }
            };
            fetchAdmins();
        }
    }, [role, serverId]);

    const handleDownloadTemplate = () => {
        const csvContent = "Name,Email,Phone Number\n";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${role}_upload_template.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- 2. CSV PARSER & SMART AUTO-GENERATOR ---
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const rows = results.data;
                const processedRows = rows.map(row => {
                    // Forgiving mapping: checks for our clean headers OR technical headers
                    const fullName = (row['Name'] || row.fullName || row.full_name || '').trim();
                    const phone = (row['Phone Number'] || row.phoneNumber || row.phone || '').trim();
                    let email = (row['Email'] || row.email || '').trim();
                    
                    // Logic: Auto-generate dummy email ONLY for Service Users if left blank
                    if (role === 'serviceUser' && !email && fullName) {
                        const cleanName = fullName.replace(/[^a-zA-Z]/g, '').toLowerCase();
                        email = `${cleanName}@healthautomatedserviceuser.co.uk`;
                    }

                    // Generate secure temporary passcode
                    const randomNum = Math.floor(10000 + Math.random() * 90000);
                    const passcode = `Architecture${randomNum}!`;

                    // Generate suggested username (Backend will still ensure absolute uniqueness)
                    const baseUsername = fullName.replace(/\s+/g, '') || 'User';
                    const phoneTail = phone.replace(/\D/g, '').slice(-3) || '000';
                    const suggestedUsername = `${baseUsername}${phoneTail}`;

                    return { fullName, phoneNumber: phone, email, username: suggestedUsername, passcode };
                }).filter(r => r.fullName !== '');

                setParsedData(processedRows);
            }
        });
    };

    // --- Handle Cell Edit ---
    const handleCellEdit = (index, field, value) => {
        const updated = [...parsedData];
        updated[index][field] = value;
        setParsedData(updated);
    };

    const executeBulkCreate = async () => {
        if (parsedData.length === 0) return alert("No valid data to import.");
        
        const missingNames = parsedData.some(r => !r.fullName || r.fullName.trim() === '');
        if (missingNames) return alert("All rows must have a Name.");

        if (role !== 'serviceUser') {
            const missingEmails = parsedData.some(r => !r.email || r.email.trim() === '');
            if (missingEmails) return alert("All staff rows must have an Email address.");
        }

        setIsLoading(true);

        // INJECT THE BATCH DEFAULTS INTO THE PAYLOAD
        const payloadArray = parsedData.map(row => {
            const rowPayload = { ...row };
            if (role === 'employee') {
                rowPayload.jobTitle = batchSettings.jobTitle;
                rowPayload.assignedAdminId = batchSettings.assignedAdminId;
            }
            if (role === 'serviceUser') {
                rowPayload.dob = batchSettings.dob;
                rowPayload.firstLineOfAddress = batchSettings.addressText;
            }
            return rowPayload;
        });

        try {
            const response = await bulkCreateUsersAPI({ targetServerId: serverId, usersArray: payloadArray, role: role });
            const { successCount, createdUsers, errors } = response.data.data;
            setResults({ successCount, createdUsers, errors });
            if (successCount > 0) refreshList(); 
        } catch (error) {
            console.error("Bulk Upload Error:", error);
            alert("Upload failed: " + (error.message || "Server Error"));
        } finally {
            setIsLoading(false);
        }
    };

    // --- 4. PDF GENERATOR ---
    const downloadPDF = () => {
        if (!results || !results.createdUsers) return;
        const doc = new jsPDF();
        
        doc.setFontSize(18);
        doc.text(`Created ${role.toUpperCase()} Credentials`, 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

        const tableColumn = ["Name", "Email", "Phone", "Username", "Password"];
        const tableRows = results.createdUsers.map(user => [
            user.fullName || "N/A",
            user.email || "N/A",
            user.phoneNumber || "N/A",
            user.newUsername,
            user.plainTextPassword
        ]);

        autoTable(doc, {
            head: [tableColumn], body: tableRows, startY: 35, theme: 'grid', styles: { fontSize: 9, cellPadding: 3 }, headStyles: { fillColor: [43, 108, 176] }, 
        });

        doc.save(`${role}_credentials_${new Date().getTime()}.pdf`);
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0, color: '#2d3748', textTransform: 'capitalize' }}>Bulk Import {role}s</h2>
                    <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#a0aec0' }}>&times;</button>
                </div>

                {!results ? (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        
                        <div style={{ backgroundColor: '#ebf8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bee3f8' }}>
                            <p style={{ margin: '0 0 10px 0', color: '#2b6cb0', fontWeight: 'bold' }}>Step 1: Use the simple 3-column template</p>
                            <button onClick={handleDownloadTemplate} style={{ padding: '8px 16px', backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '4px', cursor: 'pointer', marginRight: '15px', fontWeight: 'bold' }}>
                                ⬇️ Download Template (Name, Email, Phone)
                            </button>
                            <span style={{ fontSize: '14px', color: '#4a5568' }}>Emails are {role === 'serviceUser' ? 'optional' : 'REQUIRED'}.</span>
                        </div>

                        {/* --- BATCH DEFAULTS PANEL --- */}
                        <div style={{ backgroundColor: '#fffaf0', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #feebc8' }}>
                            <p style={{ margin: '0 0 10px 0', color: '#c05621', fontWeight: 'bold' }}>Step 2: Batch Settings (Applied to all rows)</p>

                            {role === 'employee' && (
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Job Title</label>
                                        <select value={batchSettings.jobTitle} onChange={(e) => setBatchSettings({...batchSettings, jobTitle: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                                            {JOB_TITLES_FALLBACK.map(title => <option key={title} value={title}>{title}</option>)}
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Assigned Admin</label>
                                        <select value={batchSettings.assignedAdminId} onChange={(e) => setBatchSettings({...batchSettings, assignedAdminId: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                                            <option value="">Unassigned</option>
                                            {admins.map(a => <option key={a.id} value={a.username}>{a.fullName} ({a.username})</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {role === 'serviceUser' && (
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Default Date of Birth</label>
                                        <input type="date" value={batchSettings.dob} onChange={(e) => setBatchSettings({...batchSettings, dob: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>Default Address</label>
                                        <input type="text" value={batchSettings.addressText} onChange={(e) => setBatchSettings({...batchSettings, addressText: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                                    </div>
                                </div>
                            )}

                            {role === 'admin' && (
                                <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>Admins do not require additional batch settings.</p>
                            )}
                        </div>

                        <div style={{ backgroundColor: '#f7fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                            <p style={{ margin: '0 0 10px 0', color: '#4a5568', fontWeight: 'bold' }}>Step 3: Upload CSV Data</p>
                            <input type="file" accept=".csv" onChange={handleFileUpload} style={{ padding: '10px', width: '100%', cursor: 'pointer' }} />
                        </div>

                        {/* --- Editable Input Fields in Preview Table --- */}
                        {parsedData.length > 0 && (
                            <div>
                                <h3 style={{ fontSize: '16px', color: '#2d3748', marginBottom: '10px' }}>Preview ({parsedData.length} entries)</h3>
                                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '250px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                        <thead style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0 }}>
                                            <tr>
                                                <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
                                                <th style={{ padding: '10px', textAlign: 'left' }}>Email</th>
                                                <th style={{ padding: '10px', textAlign: 'left' }}>Phone</th>
                                                <th style={{ padding: '10px', textAlign: 'left', color: '#718096' }}>Auto-Username</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsedData.map((row, index) => (
                                                <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                    <td style={{ padding: '8px' }}>
                                                        <input 
                                                            type="text" 
                                                            value={row.fullName} 
                                                            onChange={(e) => handleCellEdit(index, 'fullName', e.target.value)} 
                                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e0', borderRadius: '4px' }} 
                                                        />
                                                    </td>
                                                    <td style={{ padding: '8px' }}>
                                                        <input 
                                                            type="text" 
                                                            value={row.email} 
                                                            onChange={(e) => handleCellEdit(index, 'email', e.target.value)} 
                                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e0', borderRadius: '4px' }} 
                                                        />
                                                    </td>
                                                    <td style={{ padding: '8px' }}>
                                                        <input 
                                                            type="text" 
                                                            value={row.phoneNumber} 
                                                            onChange={(e) => handleCellEdit(index, 'phoneNumber', e.target.value)} 
                                                            style={{ width: '100%', padding: '6px', border: '1px solid #cbd5e0', borderRadius: '4px' }} 
                                                        />
                                                    </td>
                                                    <td style={{ padding: '8px', color: '#718096', fontWeight: '500' }}>{row.username}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ fontSize: '50px', marginBottom: '10px' }}>{results.successCount > 0 ? '✅' : '❌'}</div>
                        <h2 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Import Complete</h2>
                        <p style={{ fontSize: '18px', color: '#4a5568', margin: '0 0 20px 0' }}>Successfully created <strong style={{ color: '#38a169' }}>{results.successCount}</strong> accounts.</p>
                        
                        {results.errors && results.errors.length > 0 && (
                            <div style={{ width: '100%', backgroundColor: '#fff5f5', padding: '15px', borderRadius: '8px', border: '1px solid #fed7d7', marginBottom: '20px', maxHeight: '150px', overflowY: 'auto' }}>
                                <p style={{ margin: '0 0 10px 0', color: '#c53030', fontWeight: 'bold' }}>{results.errors.length} rows failed:</p>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#c53030', fontSize: '14px' }}>
                                    {results.errors.map((err, i) => <li key={i}><strong>{err.name}</strong>: {err.reason}</li>)}
                                </ul>
                            </div>
                        )}

                        {results.successCount > 0 && (
                            <button onClick={downloadPDF} style={{ padding: '12px 24px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(49, 130, 206, 0.3)' }}>
                                📄 Download PDF
                            </button>
                        )}
                    </div>
                )}

                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={closeModal} style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', color: '#4a5568' }}>
                        {results ? 'Close' : 'Cancel'}
                    </button>
                    {!results && (
                        <button 
                            onClick={executeBulkCreate} 
                            disabled={isLoading || parsedData.length === 0} 
                            style={{ 
                                padding: '10px 20px', 
                                backgroundColor: parsedData.length > 0 ? '#38a169' : '#a0aec0', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '6px', 
                                cursor: (isLoading || parsedData.length === 0) ? 'not-allowed' : 'pointer', 
                                fontWeight: 'bold' 
                            }}>
                            {isLoading ? 'Creating Accounts...' : (parsedData.length > 0 ? `Create ${parsedData.length} Accounts` : 'Import Accounts')}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default BulkUploadModal;